import { Link } from "@tanstack/react-router";
import { BadgeCheck, Shield, Lock, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/funnel-track";
import { getVariant, EXPERIMENTS } from "@/lib/ab";
import { useMemo } from "react";

type Props = {
  trigger: React.ReactNode;
  source?: string;
};

type VariantCopy = {
  title: string;
  description: string;
  guarantee: string;
  primaryCta: string;
  ctaPlacement: "top" | "bottom"; // varies CTA position within the modal
};

const VARIANTS: Record<"A" | "B", VariantCopy> = {
  A: {
    title: "How we keep you safe",
    description: "Every booking on Peopels is protected by independent safeguards — so you can hire with confidence.",
    guarantee: "Not happy with the job? Our Happiness Guarantee re-books or refunds you within 48 hours — no questions asked.",
    primaryCta: "Post a job free",
    ctaPlacement: "bottom",
  },
  B: {
    title: "Vetted Helpers. Escrow-protected payments.",
    description: "We verify every Helper and never release your money until you're satisfied.",
    guarantee: "If anything goes wrong, we re-book or refund within 48 hours — guaranteed.",
    primaryCta: "Get a free quote in 30 seconds",
    ctaPlacement: "top",
  },
};

/**
 * "How we keep you safe" modal — explains KYC verification and Stripe escrow.
 * Runs an A/B test on title/description/CTA placement; variant is recorded on every event
 * so the admin dashboard can report uplift in trust-strip clicks and helper signups.
 */
export function SafetyModal({ trigger, source = "hero_trust_strip" }: Props) {
  const variant = useMemo(() => getVariant(EXPERIMENTS.SAFETY_MODAL_COPY), []);
  const copy = VARIANTS[variant];

  const CtaRow = (
    <div className="flex flex-wrap gap-2">
      <Button
        asChild
        className="rounded-xl"
        onClick={() => void track("safety_modal_cta_click", { source, variant, cta: "post_job" })}
      >
        <Link to="/post-job">{copy.primaryCta} <ArrowRight className="ml-1 h-4 w-4" /></Link>
      </Button>
      <Button asChild variant="outline" className="rounded-xl">
        <Link to="/how-it-works">Trust &amp; safety policy</Link>
      </Button>
      <Button asChild variant="ghost" className="rounded-xl">
        <Link to="/terms">Terms</Link>
      </Button>
      <Button asChild variant="ghost" className="rounded-xl">
        <Link to="/privacy">Privacy</Link>
      </Button>
    </div>
  );

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) void track("safety_modal_view", { source, variant });
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        {copy.ctaPlacement === "top" && <div className="mt-3">{CtaRow}</div>}

        <ul className="mt-2 space-y-4">
          <SafetyItem
            icon={<BadgeCheck className="h-5 w-5" />}
            title="KYC-verified Helpers"
            body="Every Helper passes government-ID and DBS background checks before their first job. We re-verify documents annually."
          />
          <SafetyItem
            icon={<Lock className="h-5 w-5" />}
            title="Stripe-secured escrow payments"
            body="Your card is charged when you book, but funds are held by Stripe and only released to the Helper once you confirm the work is done."
          />
          <SafetyItem
            icon={<Shield className="h-5 w-5" />}
            title="Happiness guarantee"
            body="If something's not right, we re-book or refund you within 48 hours."
          />
        </ul>

        <div className="mt-4 rounded-xl bg-accent/60 p-3 text-sm text-foreground">
          {copy.guarantee}
        </div>

        {copy.ctaPlacement === "bottom" && <div className="mt-4">{CtaRow}</div>}
      </DialogContent>
    </Dialog>
  );
}

function SafetyItem({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-500/10 text-emerald-600">{icon}</span>
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
      </div>
    </li>
  );
}
